import { Link } from 'react-router-dom'
import { ArrowRight, Monitor, Smartphone, Zap, Shield, Search, Palette, X, ExternalLink, Calendar, Phone, TrendingUp } from 'lucide-react'
import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import FAQAccordion from '../../components/FAQAccordion'
import CalComModal from '../../components/CalComModal'
import CallbackModal from '../../components/CallbackModal'
import { useLanguage } from '../../contexts/LanguageContext'
import translations from '../../locales/translations'
import { useMarqueeScroll } from '../../hooks/useMarqueeScroll'
import { getImageDimensions } from '../../utils/imageDimensions'

const WebDesign = () => {
  const { t, language } = useLanguage()
  const [selectedSite, setSelectedSite] = useState<number | null>(null)
  const [isCalComModalOpen, setIsCalComModalOpen] = useState(false)
  const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false)
  const marqueeRef = useMarqueeScroll()
  
  const pageContent = useMemo(() => {
    return (translations as any)[language].servicePages.webDesign
  }, [language])

  const portfolioSites = useMemo(() => {
    const previews = ['/images/portfolio/smartiq.webp', '/images/portfolio/mcdbag.webp', '/images/portfolio/veela.webp', '/images/portfolio/tasarlabas.webp', '/images/portfolio/daphnesandals.webp', '/images/portfolio/cheesecake.webp', '/images/portfolio/firuzeshowroom.webp', '/images/portfolio/dorehareketegitimi.webp']
    return pageContent.portfolioSites.map((site: any, index: number) => ({
      id: index + 1,
      name: site.name,
      description: site.description,
      url: site.url,
      preview: previews[index] || '/images/portfolio/default.webp'
    }))
  }, [pageContent])

  const openModal = (siteId: number) => {
    setSelectedSite(siteId)
  }

  const closeModal = () => {
    setSelectedSite(null)
  }

  const selectedSiteData = selectedSite ? portfolioSites.find(s => s.id === selectedSite) : null
  const features = useMemo(() => {
    return pageContent.features.map((feature: any, index: number) => ({
      icon: [Monitor, Zap, Search, Shield, Palette, Smartphone][index],
      title: feature.title,
      description: feature.description
    }))
  }, [pageContent])

  const seoData = useMemo(() => {
    const title = language === 'tr' 
      ? `${pageContent.title} | Web Tasarım Hizmetleri - Thinklyze`
      : `${pageContent.title} | Web Design Services - Thinklyze`
    
    const description = language === 'tr'
      ? `${pageContent.description} Modern, responsive ve dönüşüm odaklı web tasarım hizmetleri. İstanbul, Türkiye.`
      : `${pageContent.description} Modern, responsive and conversion-focused web design services. Istanbul, Turkey.`
    
    const keywords = language === 'tr'
      ? 'Web tasarım, web sitesi tasarımı, responsive web tasarım, e-ticaret sitesi, kurumsal web sitesi, UI/UX tasarım, web geliştirme, İstanbul, Türkiye'
      : 'Web design, website design, responsive web design, e-commerce website, corporate website, UI/UX design, web development, Istanbul, Turkey'
    
    return { title, description, keywords }
  }, [pageContent, language])

  const structuredData = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Web Design",
    "provider": {
      "@type": "Organization",
      "name": "Thinklyze",
      "url": "https://thinklyze.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Turkey"
    },
    "description": seoData.description,
    "name": seoData.title,
    "url": "https://thinklyze.com/services/web-design"
  }), [seoData])

  return (
    <>
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href="https://thinklyze.com/services/web-design" />
        <link rel="alternate" hreflang="tr" href="https://thinklyze.com/services/web-design" />
        <link rel="alternate" hreflang="en" href="https://thinklyze.com/services/web-design?lang=en" />
        <link rel="alternate" hreflang="x-default" href="https://thinklyze.com/services/web-design" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content="https://thinklyze.com/services/web-design" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://thinklyze.com/images/logo_siyah.webp" />
        <meta property="og:locale" content={language === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={language === 'tr' ? 'en_US' : 'tr_TR'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="geo.region" content="TR-34" />
        <meta name="geo.placename" content="İstanbul" />
        <meta name="geo.position" content="41.0082;28.9784" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
    <div className="pt-16 bg-[#f9f9f9]">
      <section className="py-24 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.05)] p-8 md:p-16">
            <div className="flex flex-col lg:flex-row gap-16 items-center">
              <div className="flex-1 space-y-6">
                <span className="inline-block text-[13px] font-medium text-[#666] bg-[#f3f3f3] px-3 py-1 rounded-full">{pageContent.tag}</span>
                <h1 className="text-[clamp(32px,5vw,48px)] font-bold text-[#111] leading-[1.1]">{pageContent.title}</h1>
                <p className="text-lg text-[#555] max-w-[500px] leading-relaxed">{pageContent.description}</p>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    onClick={() => setIsCalComModalOpen(true)}
                    className="inline-flex items-center justify-center bg-black text-white px-7 py-3.5 rounded-full text-[15px] font-medium hover:bg-gray-800 transition-colors duration-200"
                  >
                    {t('servicePages.common.scheduleMeeting')}
                    <Calendar className="w-4 h-4 ml-2" />
                  </button>
                  <button
                    onClick={() => setIsCallbackModalOpen(true)}
                    className="inline-flex items-center justify-center bg-transparent text-gray-700 border border-gray-300 px-7 py-3.5 rounded-full text-[15px] font-medium hover:bg-gray-50 transition-colors duration-200"
                  >
                    {t('servicePages.common.callMe')}
                    <Phone className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </div>
              <div className="flex-1 max-w-[500px] w-full">
                <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <img 
                      src="/images/samed-ordueri.jpeg" 
                      alt="Samed Ordueri" 
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-semibold text-[#111] text-base">Samed Ordueri</div>
                      <div className="text-sm text-[#666]">
                        {language === 'tr'
                          ? 'Web Tasarımcısı'
                          : language === 'en'
                          ? 'Web Designer'
                          : 'Веб-дизайнер'}
                      </div>
                    </div>
                  </div>
                  <h3 className="font-bold text-[#111] text-lg mb-2">
                    {language === 'tr'
                      ? 'Web Tasarım Danışmanlığı'
                      : language === 'en'
                      ? 'Web Design Consultation'
                      : 'Консультация по веб‑дизайну'}
                  </h3>
                  <p className="text-sm text-[#666] mb-6">
                    {language === 'tr'
                      ? 'Modern ve duyarlı web çözümleri.'
                      : language === 'en'
                      ? 'Modern and responsive web solutions.'
                      : 'Современные и адаптивные веб‑решения.'}
                  </p>
                  <div className="space-y-3 mb-6 text-sm text-[#555]">
                    <div className="flex items-center gap-2">
                      <span>
                        {language === 'tr'
                          ? '30 dk'
                          : language === 'en'
                          ? '30 mins'
                          : '30 мин'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M15 10l-4 4-4-4" strokeWidth="2" strokeLinecap="round"/></svg>
                      <span>Google Meet</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-100">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-indigo-600">95+</div>
                      <div className="text-xs text-[#999] mt-1">
                        {language === 'tr'
                          ? 'Hız'
                          : language === 'en'
                          ? 'Speed'
                          : 'Скорость'}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-indigo-600">100%</div>
                      <div className="text-xs text-[#999] mt-1">
                        {language === 'tr'
                          ? 'Mobil'
                          : language === 'en'
                          ? 'Mobile'
                          : 'Мобайл'}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-indigo-600">A+</div>
                      <div className="text-xs text-[#999] mt-1">SEO</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-8 border-t border-gray-200 border-b border-gray-200">
        <div className="max-w-[1200px] mx-auto px-6">
          {/* Client Logos Marquee - Single Row */}
          <div ref={marqueeRef} className="w-full overflow-hidden md:overflow-hidden overflow-x-auto md:overflow-x-hidden py-4 relative marquee-container-mobile">
            {/* Fade gradients - hidden on mobile */}
            <div className="hidden md:block absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#f9f9f9] to-transparent z-10 pointer-events-none"></div>
            <div className="hidden md:block absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#f9f9f9] to-transparent z-10 pointer-events-none"></div>
            <div className="flex animate-marquee-right gap-4 md:gap-4">
              {/* First set */}
              {[
                '/images/brands/ref1.webp',
                '/images/brands/ref2.webp',
                '/images/brands/ref3.webp',
                '/images/brands/ref4.webp',
                '/images/brands/ref6.webp',
                '/images/brands/ref7.webp',
                '/images/brands/ref8.webp',
                '/images/brands/veela1.webp',
                '/images/brands/daphnesandals.webp',
              ].map((logo, index) => {
                const dimensions = getImageDimensions(logo)
                return (
                  <div key={`logo-${index}`} className="marquee-item-mobile flex-shrink-0 bg-white rounded-xl p-3 md:p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                    <img 
                      src={logo} 
                      alt={`Referans ${index + 1}`} 
                      loading="lazy"
                      decoding="async"
                      width={dimensions?.width}
                      height={dimensions?.height}
                      className="h-10 md:h-12 max-w-[120px] md:max-w-[140px] object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                    />
                  </div>
                )
              })}
              {/* Duplicate set for seamless loop - hidden on mobile */}
              {[
                '/images/brands/ref1.webp',
                '/images/brands/ref2.webp',
                '/images/brands/ref3.webp',
                '/images/brands/ref4.webp',
                '/images/brands/ref6.webp',
                '/images/brands/ref7.webp',
                '/images/brands/ref8.webp',
                '/images/brands/veela1.webp',
                '/images/brands/daphnesandals.webp',
              ].map((logo, index) => {
                const dimensions = getImageDimensions(logo)
                return (
                  <div key={`logo-dup-${index}`} className="hidden md:flex flex-shrink-0 bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                    <img 
                      src={logo} 
                      alt={`Referans ${index + 1}`} 
                      loading="lazy"
                      decoding="async"
                      width={dimensions?.width}
                      height={dimensions?.height}
                      className="h-12 max-w-[140px] object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                    />
                  </div>
                )
              })}
            </div>
          </div>

          {/* Title Below Logos */}
          <div className="text-center mt-4">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gray-50 rounded-full border border-gray-100">
              <TrendingUp className="w-4 h-4 text-gray-400" />
              <p className="text-sm md:text-base text-gray-700 font-medium font-jakarta">
                {t('servicePages.common.trustedBy')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="bg-white py-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-[#666] mb-4">{pageContent.portfolioSection.badge}</p>
            <h2 className="text-[clamp(32px,5vw,48px)] font-bold text-[#111] leading-[1.1] mb-6">{pageContent.portfolioSection.title}</h2>
            <p className="text-lg text-[#555] max-w-3xl mx-auto mb-8 leading-relaxed">
              {pageContent.portfolioSection.subtitle}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioSites.map((site) => (
              <div
                key={site.id}
                onClick={() => openModal(site.id)}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 group"
              >
                {/* Preview Image */}
                <div className="relative aspect-video bg-gray-100 overflow-hidden">
                  <img
                    src={site.preview}
                    alt={site.name}
                    loading="lazy"
                    decoding="async"
                    width={getImageDimensions(site.preview)?.width}
                    height={getImageDimensions(site.preview)?.height}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      // Fallback if image doesn't exist - show placeholder
                      const target = e.target as HTMLImageElement
                      target.src = `https://api.screenshotone.com/take?access_key=demo&url=${encodeURIComponent(site.url)}&viewport_width=1280&viewport_height=720&device_scale_factor=1&format=png&image_quality=80`
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 text-sm font-medium text-gray-900">
                      Detayları Görüntüle
                    </div>
                  </div>
                </div>
                {/* Site Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#111] mb-2">{site.name}</h3>
                  <p className="text-[#666] text-sm leading-relaxed">{site.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm text-[#666] mb-3 font-medium">{t('servicePages.common.keyBenefits')}</p>
            <h2 className="text-[clamp(28px,4vw,42px)] font-bold text-[#111] leading-[1.2] mb-4 md:mb-5">
              {pageContent.keyBenefits.title}
            </h2>
            <p className="text-base md:text-lg text-[#555] max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
              {pageContent.keyBenefits.subtitle}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-black text-white px-6 md:px-7 py-3 md:py-3.5 rounded-full text-sm md:text-[15px] font-medium hover:bg-gray-800 transition-colors duration-200"
            >
              {t('servicePages.common.getStarted')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-10 mt-12 md:mt-16">
            {pageContent.keyBenefits.benefits.map((benefit: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative bg-white rounded-2xl p-6 md:p-8 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300"
              >
                {/* Number Badge */}
                <div className="absolute -top-4 left-6 md:left-8">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold text-lg md:text-xl">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="pt-6 md:pt-8">
                  <h3 className="text-xl md:text-2xl font-bold text-[#111] mb-3 md:mb-4 leading-tight group-hover:text-blue-600 transition-colors duration-300">
                    {benefit.title}
                  </h3>
                  <p className="text-sm md:text-base text-[#555] leading-relaxed">
                    {benefit.description}
                  </p>
                </div>

                {/* Decorative Line */}
                <div className="absolute bottom-0 left-6 md:left-8 right-6 md:right-8 h-1 bg-gradient-to-r from-blue-500 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm text-[#666] mb-3 font-medium">{t('servicePages.common.additionalFeatures')}</p>
            <h2 className="text-[clamp(28px,4vw,42px)] font-bold text-[#111] leading-[1.2] mb-4 md:mb-5">
              {pageContent.additionalFeatures.title}
            </h2>
            <p className="text-base md:text-lg text-[#555] max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
              {pageContent.additionalFeatures.subtitle}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-black text-white px-6 md:px-7 py-3 md:py-3.5 rounded-full text-sm md:text-[15px] font-medium hover:bg-gray-800 transition-colors duration-200"
            >
              {t('servicePages.common.getStarted')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-12 md:mt-16">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="group bg-white rounded-xl p-6 md:p-8 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-blue-50 group-hover:to-blue-100 flex items-center justify-center mb-4 md:mb-5 transition-all duration-300">
                  <f.icon className="w-6 h-6 md:w-7 md:h-7 text-gray-700 group-hover:text-blue-600 transition-colors duration-300" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-[#111] mb-2 md:mb-3 leading-tight group-hover:text-blue-600 transition-colors duration-300">
                  {f.title}
                </h3>
                <p className="text-sm md:text-base text-[#555] leading-relaxed">
                  {f.description}
                </p>
          </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FAQAccordion
        title={pageContent.faq.title}
        subtitle={pageContent.faq.subtitle}
        faqs={pageContent.faq.items}
        onCTAClick={() => setIsCalComModalOpen(true)}
      />

      {/* Portfolio Modal */}
      <AnimatePresence>
        {selectedSiteData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="flex items-start justify-between p-4 sm:p-6 border-b border-gray-200">
                <div className="flex-1 pr-3 sm:pr-0">
                  <h2 className="text-lg sm:text-2xl font-bold text-[#111] mb-1 leading-tight">{selectedSiteData.name}</h2>
                  <p className="text-xs sm:text-sm text-[#666] leading-relaxed">{selectedSiteData.description}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                </button>
              </div>

              {/* Preview */}
              <div className="relative flex-1 bg-gray-100 overflow-hidden min-h-[200px] sm:min-h-[400px]">
                <div className="absolute inset-0 flex items-center justify-center p-2 sm:p-4">
                  <img
                    src={selectedSiteData.preview}
                    alt={selectedSiteData.name}
                    loading="lazy"
                    decoding="async"
                    width={getImageDimensions(selectedSiteData.preview)?.width}
                    height={getImageDimensions(selectedSiteData.preview)?.height}
                    className="w-full h-full object-contain rounded-lg"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.src = `https://api.screenshotone.com/take?access_key=demo&url=${encodeURIComponent(selectedSiteData.url)}&viewport_width=1280&viewport_height=720&device_scale_factor=1&format=png&image_quality=80`
                    }}
                  />
                </div>
              </div>

              {/* Footer with Visit Button */}
              <div className="p-4 sm:p-6 border-t border-gray-200 space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
                <div className="text-xs sm:text-sm text-[#666] break-all sm:break-normal">
                  <span className="font-medium hidden sm:inline">URL: </span>
                  <span className="font-medium sm:hidden">URL</span>
                  <div className="mt-1 sm:mt-0 sm:inline">{selectedSiteData.url}</div>
                </div>
                <a
                  href={selectedSiteData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-black text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors duration-200 w-full sm:w-auto text-sm sm:text-base"
                >
                  <span>{language === 'tr' ? 'Canlı Siteyi Ziyaret Et' : language === 'en' ? 'Visit Live Site' : 'Посетить сайт'}</span>
                  <ExternalLink className="w-4 h-4 flex-shrink-0" />
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modals */}
      <CalComModal 
        isOpen={isCalComModalOpen} 
        onClose={() => setIsCalComModalOpen(false)} 
      />
      <CallbackModal 
        isOpen={isCallbackModalOpen} 
        onClose={() => setIsCallbackModalOpen(false)} 
      />
    </div>
    </>
  )
}

export default WebDesign















